import { type CSSProperties, lazy, Suspense } from "react";
import type { ResumeDocument } from "../api/client";
import "./cv-base.css";
import { getCvLabels } from "./labels";

const TEMPLATES = {
  classic: lazy(() => import("./templates/classic")),
  minimal: lazy(() => import("./templates/minimal")),
  mono: lazy(() => import("./templates/mono")),
  editorial: lazy(() => import("./templates/editorial")),
  sidebar: lazy(() => import("./templates/sidebar")),
  timeline: lazy(() => import("./templates/timeline")),
  compact: lazy(() => import("./templates/compact")),
  bold: lazy(() => import("./templates/bold")),
} as const;

export type TemplateId = keyof typeof TEMPLATES;

export function isTemplateId(id: string): id is TemplateId {
  return id in TEMPLATES;
}

export function cvStyle(doc: ResumeDocument): CSSProperties {
  return { "--cv-accent": doc.theme.accent } as CSSProperties;
}

export function CvRenderer({
  doc,
  branded = false,
  className,
  id,
}: {
  doc: ResumeDocument;
  branded?: boolean;
  className?: string;
  id?: string;
}) {
  const Component = isTemplateId(doc.templateId) ? TEMPLATES[doc.templateId] : TEMPLATES.classic;
  const labels = getCvLabels(doc.locale);

  return (
    <div
      id={id}
      className={["cv-page", className].filter(Boolean).join(" ")}
      data-page={doc.theme.pageSize}
      data-font={doc.theme.fontPair}
      data-density={doc.theme.density}
      style={cvStyle(doc)}
    >
      <Suspense fallback={null}>
        <Component doc={doc} labels={labels} />
      </Suspense>
      {branded ? <div className="cv-badge">{labels.madeWith}</div> : null}
    </div>
  );
}
